import tippy, { delegate } from "tippy.js";

const TITLE_DATA_KEY = "mphbTooltip";

const OBSERVER_ROOTS =
  ".property__booking, .filter-bar, .booking-page__content, .mphb_sc_search-results-wrapper";

const DYNAMIC_TITLE_SELECTOR = ".mphb-price-period[title]";

const CALENDAR_CELL_SELECTOR = ".mphb-datepick-popup .mphb-date-cell[title]";

const sharedOptions = {
  allowHTML: true,
  placement: "top",
  offset: [0, 10],
  appendTo: () => document.body,
  theme: "ferienhausliebe",
  maxWidth: 280,
  delay: [250, 0],
};

function readTitle(el) {
  const stored = el.dataset[TITLE_DATA_KEY];
  if (stored) {
    return stored;
  }

  const title = el.getAttribute("title")?.trim();
  if (!title) {
    return "";
  }

  el.dataset[TITLE_DATA_KEY] = title;
  el.removeAttribute("title");
  return title;
}

function formatTooltipContent(text) {
  if (!text) {
    return "";
  }

  return text.includes("\n") ? text.replace(/\n+/g, "<br>") : text;
}

const SKIP_CALENDAR_TITLES = [
  /^(?:Verfügbar|Available)\s*\(\d+\)$/i,
  /^(?:Tag in der Vergangenheit|Day in the past)$/i,
];

const STRIP_CALENDAR_TITLE_PREFIXES = [
  /^(?:Verfügbar|Available)\s*\(\d+\)\s*\n?/i,
  /^(?:Tag in der Vergangenheit|Day in the past)\s*\n?/i,
];

function formatCalendarTooltipContent(text) {
  if (!text) {
    return "";
  }

  const trimmed = text.trim();
  if (SKIP_CALENDAR_TITLES.some((pattern) => pattern.test(trimmed))) {
    return "";
  }

  const stripped = STRIP_CALENDAR_TITLE_PREFIXES.reduce(
    (value, pattern) => value.replace(pattern, ""),
    trimmed,
  ).trim();

  return formatTooltipContent(stripped);
}

function initDynamicTitleTooltips(root = document) {
  const elements =
    root instanceof Element && root.matches(DYNAMIC_TITLE_SELECTOR)
      ? [root, ...root.querySelectorAll(DYNAMIC_TITLE_SELECTOR)]
      : root.querySelectorAll(DYNAMIC_TITLE_SELECTOR);

  elements.forEach((el) => {
    if (el._tippy) {
      return;
    }

    const text = readTitle(el);
    if (!text) {
      return;
    }

    tippy(el, {
      ...sharedOptions,
      content: formatTooltipContent(text),
    });
  });
}

function initCalendarTitleTooltips() {
  delegate(document.body, {
    ...sharedOptions,
    target: CALENDAR_CELL_SELECTOR,
    interactive: false,
    content(reference) {
      return formatCalendarTooltipContent(readTitle(reference));
    },
    onShow(instance) {
      if (!instance.props.content) {
        return false;
      }
    },
  });
}

function observeDynamicTitleTooltips() {
  const roots = document.querySelectorAll(OBSERVER_ROOTS);
  if (!roots.length) {
    initDynamicTitleTooltips();
    return;
  }

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) {
          continue;
        }

        if (
          (node instanceof Element && node.matches(DYNAMIC_TITLE_SELECTOR)) ||
          (node instanceof Element &&
            node.querySelector(DYNAMIC_TITLE_SELECTOR))
        ) {
          initDynamicTitleTooltips(node);
        }
      }
    }
  });

  roots.forEach((root) => {
    observer.observe(root, { childList: true, subtree: true });
  });

  initDynamicTitleTooltips();
}

function initMphbTitleTooltips() {
  initCalendarTitleTooltips();
  observeDynamicTitleTooltips();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initMphbTitleTooltips);
} else {
  initMphbTitleTooltips();
}
